import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCar,
  faCog,
  faGasPump,
  faArrowRight,
  faArrowLeft,
  faSearch,
  faChevronDown,
  faCalendarAlt,
  faTimes,
  faChevronLeft,
  faChevronRight,
  faUser,
} from "@fortawesome/free-solid-svg-icons";

import api from "../../api/http";
import NormalizedCarImage from "./NormalizedCarImage";
import { BannerSlider } from "./Banners";
import "../../styles/produs/Car.css";

const PRODUCTS_PER_PAGE = 12;

const CardImageCarousel = ({ images, alt }) => {
  const [idx, setIdx] = useState(0);
  const [dragX, setDragX] = useState(0);
  const total = images.length;

  const pointerIdRef = useRef(null);
  const startXRef = useRef(0);
  const startYRef = useRef(0);
  const movedRef = useRef(false);
  const swipedRef = useRef(false);

  if (total === 0) {
    return (
        <div className="srCars_imgWrap">
          <div className="srCars_imgPh">Fără imagine</div>
        </div>
    );
  }

  const onPointerDown = (e) => {
    if (total <= 1) return;
    // only track touch/pen gestures (mouse drag is optional; keep click navigation normal)
    if (e.pointerType === "mouse") return;

    pointerIdRef.current = e.pointerId;
    startXRef.current = e.clientX;
    startYRef.current = e.clientY;
    movedRef.current = false;
    swipedRef.current = false;
    setDragX(0);

    // keep receiving move events even if finger leaves element
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    if (pointerIdRef.current == null) return;
    if (e.pointerId !== pointerIdRef.current) return;

    const dx = e.clientX - startXRef.current;
    const dy = e.clientY - startYRef.current;

    // if user is scrolling vertically, do nothing (allow normal page scroll)
    if (!movedRef.current) {
      if (Math.abs(dy) > 10 && Math.abs(dy) > Math.abs(dx)) return;
    }

    movedRef.current = true;
    setDragX(dx);
  };

  const finishGesture = () => {
    const threshold = 50;
    if (Math.abs(dragX) >= threshold) {
      swipedRef.current = true;
      setIdx((i) => {
        if (dragX < 0) return (i + 1) % total;
        return (i - 1 + total) % total;
      });
    }
    setDragX(0);
    pointerIdRef.current = null;
    movedRef.current = false;
  };

  const onPointerUp = (e) => {
    if (pointerIdRef.current == null) return;
    if (e.pointerId !== pointerIdRef.current) return;
    finishGesture();
  };

  const onPointerCancel = () => {
    if (pointerIdRef.current == null) return;
    setDragX(0);
    pointerIdRef.current = null;
    movedRef.current = false;
  };

  const prev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => (i - 1 + total) % total);
  };
  const next = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx((i) => (i + 1) % total);
  };
  const goTo = (e, i) => {
    e.preventDefault();
    e.stopPropagation();
    setIdx(i);
  };

  return (
      <div
          className="srCars_imgWrap"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onClickCapture={(e) => {
            // prevent Link navigation if this tap was actually a swipe gesture
            if (swipedRef.current) {
              e.preventDefault();
              e.stopPropagation();
              swipedRef.current = false;
            }
          }}
      >
        <div
            className="srCars_imgTrack"
            style={{ transform: `translateX(calc(-${idx * 100}% + ${dragX}px))` }}
        >
          {images.map((img, i) => (
              <div key={i} className="srCars_imgSlide">
                <NormalizedCarImage
                    src={img.image}
                    className="srCars_img"
                    variant="card"
                    alt={`${alt} — ${i + 1}`}
                />
              </div>
          ))}
        </div>

        {total > 1 && (
            <>
              <button
                  className="srCars_imgArrow srCars_imgArrow--prev"
                  onClick={prev}
                  aria-label="Imagine anterioară"
                  type="button"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                  className="srCars_imgArrow srCars_imgArrow--next"
                  onClick={next}
                  aria-label="Imagine următoare"
                  type="button"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </>
        )}

        {total > 1 && (
            <ul className="srCars_imgDots" aria-label="Selectează imaginea">
              {images.map((_, i) => (
                  <li
                      key={i}
                      className={`srCars_imgDot${i === idx ? " is-active" : ""}`}
                      onClick={(e) => goTo(e, i)}
                      aria-label={`Imaginea ${i + 1}`}
                  />
              ))}
            </ul>
        )}
      </div>
  );
};

/* -------- helpers used by the new card layout -------- */
const gearboxLabel = (v) =>
    v === 0 ? "Manual" : v === 1 ? "Automat" : "Nespecificat";

const fuelLabel = (v) =>
    v === 0
        ? "Diesel"
        : v === 1
            ? "Hybrid"
            : v === 2
                ? "Benzină"
                : v === 3
                    ? "Electric"
                    : v === 4
                        ? "Petrol-Hybrid"
                        : v === 5
                            ? "Petrol-Gaz"
                            : "Nespecificat";

const ProductList = () => {
  const [products, setProducts]   = useState([]);
  const [count, setCount]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isFiltering, setIsFiltering]           = useState(false);
  const [noFilteredProductsMessage, setNoFilteredProductsMessage] = useState(false);

  const [producers, setProducers]           = useState([]);
  const [motorOptions, setMotorOptions]     = useState([]);
  const [cutiaOptions, setCutiaOptions]     = useState([]);
  const [usiOptions, setUsiOptions]         = useState([]);
  const [caroserieOptions, setCaroserieOptions] = useState([]);

  const [yearRange, setYearRange] = useState({ min: "", max: "" });
  const [nameQuery, setNameQuery] = useState("");
  const [filters, setFilters]     = useState({
    producator: "", cutia: "", motor: "", numar_usi: "", caroserie: "",
  });

  const productListRef = useRef(null);

  const totalPages = useMemo(
      () => Math.max(1, Math.ceil(count / PRODUCTS_PER_PAGE)),
      [count]
  );
  const pageNumbers = useMemo(
      () => Array.from({ length: totalPages }, (_, i) => i + 1),
      [totalPages]
  );

  const scrollToList = () => {
    if (productListRef.current) {
      window.scrollTo({ top: productListRef.current.offsetTop - 12, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const loadPage = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/produs/car", {
          params: { page: currentPage, page_size: PRODUCTS_PER_PAGE },
        });
        const data    = res.data;
        const results = Array.isArray(data?.results) ? data.results
            : Array.isArray(data)          ? data
                : [];
        setProducts(results);
        setCount(Number(data?.count ?? results.length));
      } catch {
        setError("Eroare la încărcarea produselor.");
      } finally {
        setLoading(false);
      }
    };
    if (!isFiltering) { loadPage(); scrollToList(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, isFiltering]);

  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const res  = await api.get("/produs/car", { params: { page_size: 999999 } });
        const data = res.data;
        const all  = Array.isArray(data?.results) ? data.results
            : Array.isArray(data)          ? data
                : [];
        setProducers([...new Set(all.map((p) => p.producator).filter(Boolean))]);
        setCutiaOptions([...new Set(all.map((p) => p.cutia))]);
        setMotorOptions([...new Set(all.map((p) => p.motor))]);
        setUsiOptions([...new Set(all.map((p) => p.numar_usi))]);
        setCaroserieOptions([...new Set(all.map((p) => p.caroserie))]);
        const years = all.map((p) => p.an).filter((x) => typeof x === "number");
        if (years.length) setYearRange({ min: Math.min(...years), max: Math.max(...years) });
      } catch {}
    };
    loadFilterOptions();
  }, []);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleFilterApplyClick = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      setIsFiltering(true);
      setCurrentPage(1);
      const params = {
        ...filters,
        an_min: yearRange.min,
        an_max: yearRange.max,
        name:   nameQuery || undefined,
      };
      const res     = await api.get("/produs/filter/", { params });
      const data    = res.data;
      const results = Array.isArray(data?.results) ? data.results
          : Array.isArray(data)          ? data
              : [];
      setFilteredProducts(results);
      if (results.length === 0) {
        setNoFilteredProductsMessage(true);
        setTimeout(() => setNoFilteredProductsMessage(false), 5000);
      }
    } catch {
      setError("Eroare la filtrarea produselor.");
    } finally {
      setLoading(false);
      scrollToList();
    }
  };

  const clearFilters = () => {
    setIsFiltering(false);
    setFilteredProducts([]);
    setNoFilteredProductsMessage(false);
    setCurrentPage(1);
    setNameQuery("");
    setFilters({ producator: "", cutia: "", motor: "", numar_usi: "", caroserie: "" });
  };

  const baseList     = isFiltering ? filteredProducts : products;
  const listToRender = useMemo(() => {
    const q = (nameQuery || "").trim().toLowerCase();
    if (!q) return baseList;
    return baseList.filter((p) =>
        `${p.producator ?? ""} ${p.name ?? ""}`.trim().toLowerCase().includes(q)
    );
  }, [baseList, nameQuery]);

  const hasActiveFilters = isFiltering || nameQuery.trim();

  if (loading) {
    return (
        <div className="srCars_page">
          <BannerSlider />
          <div className="srCars_container">
            <div className="srCars_center" style={{ minHeight: "40vh" }}>
              <div className="srCars_spinner" role="status" aria-label="Se încarcă..." />
            </div>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="srCars_page">
          <BannerSlider />
          <div className="srCars_container">
            <div className="srCars_center" style={{ minHeight: "40vh" }}>
              <div className="srCars_error">{error}</div>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="srCars_page">
        {/*<BannerSlider />*/}

        <div className="srCars_container" id="products">

          <section className="srCarsFilters" ref={productListRef} aria-label="Filtre produse">
            <div className="srCarsFilters_head">
              <h2 className="srCarsFilters_title">Drumul tău începe cu mașina potrivită</h2>
              <p className="srCarsFilters_subtitle">Filtrează din flota noastră disponibilă</p>
            </div>

            <form className="srCarsFilters_grid" onSubmit={handleFilterApplyClick}>

              <div className="srCarsFilters_searchWrap">
              <span className="srCarsFilters_searchIcon" aria-hidden="true">
                <FontAwesomeIcon icon={faSearch} />
              </span>
                <input
                    className="srCarsFilters_input"
                    type="text"
                    placeholder="Caută după marcă sau model (ex: Ford, Kuga...)"
                    value={nameQuery}
                    onChange={(e) => setNameQuery(e.target.value)}
                    aria-label="Caută după nume"
                />
              </div>

              <div className="srCarsFilters_selectWrap">
                <select
                    className="srCarsFilters_select"
                    name="producator"
                    value={filters.producator}
                    onChange={handleFilterChange}
                    aria-label="Producător"
                >
                  <option value="">Producător</option>
                  {producers.map((p, i) => (
                      <option key={i} value={p}>{p}</option>
                  ))}
                </select>
                <span className="srCarsFilters_chevron" aria-hidden="true">
                <FontAwesomeIcon icon={faChevronDown} />
              </span>
              </div>

              <div className="srCarsFilters_selectWrap">
                <select
                    className="srCarsFilters_select"
                    name="cutia"
                    value={filters.cutia}
                    onChange={handleFilterChange}
                    aria-label="Cutia vitezelor"
                >
                  <option value="">Cutii</option>
                  {cutiaOptions.map((cutia, idx) => (
                      <option key={idx} value={cutia}>
                        {cutia === 0 ? "Manual" : cutia === 1 ? "Automat" : "Nespecificat"}
                      </option>
                  ))}
                </select>
                <span className="srCarsFilters_chevron" aria-hidden="true">
                <FontAwesomeIcon icon={faChevronDown} />
              </span>
              </div>

              <div className="srCarsFilters_selectWrap">
                <select
                    className="srCarsFilters_select"
                    name="motor"
                    value={filters.motor}
                    onChange={handleFilterChange}
                    aria-label="Motor"
                >
                  <option value="">Motor</option>
                  {motorOptions.map((motor, idx) => (
                      <option key={idx} value={motor}>
                        {motor === 0
                            ? "Diesel"
                            : motor === 1
                                ? "Hybrid"
                                : motor === 2
                                    ? "Petrol"
                                    : motor === 3
                                        ? "Electric"
                                        : motor === 4
                                            ? "Petrol-Hybrid"
                                            : motor === 5
                                                ? "Petrol-Gaz"
                                                : "Nespecificat"}
                      </option>
                  ))}
                </select>
                <span className="srCarsFilters_chevron" aria-hidden="true">
                <FontAwesomeIcon icon={faChevronDown} />
              </span>
              </div>

              <div className="srCarsFilters_selectWrap">
                <select
                    className="srCarsFilters_select"
                    name="numar_usi"
                    value={filters.numar_usi}
                    onChange={handleFilterChange}
                    aria-label="Numerele de Uși"
                >
                  <option value="">Numerele de Uși</option>
                  {usiOptions.map((usi, idx) => (
                      <option key={idx} value={usi}>
                        {usi === 0 ? "3" : usi === 1 ? "5" : "Nespecificat"}
                      </option>
                  ))}
                </select>
                <span className="srCarsFilters_chevron" aria-hidden="true">
                <FontAwesomeIcon icon={faChevronDown} />
              </span>
              </div>

              <div className="srCarsFilters_selectWrap">
                <select
                    className="srCarsFilters_select"
                    name="caroserie"
                    value={filters.caroserie}
                    onChange={handleFilterChange}
                    aria-label="Caroserie"
                >
                  <option value="">Caroserie</option>
                  {caroserieOptions.map((c, idx) => (
                      <option key={idx} value={c}>
                        {c === 0
                            ? "Van"
                            : c === 1
                                ? "Universal"
                                : c === 2
                                    ? "Minivan"
                                    : c === 3
                                        ? "Roadster"
                                        : c === 4
                                            ? "SUV"
                                            : c === 5
                                                ? "Cabriolet"
                                                : c === 6
                                                    ? "Microvan"
                                                    : c === 7
                                                        ? "Pickup"
                                                        : c === 8
                                                            ? "Sedan"
                                                            : c === 9
                                                                ? "Crossover"
                                                                : c === 10
                                                                    ? "Hatchback"
                                                                    : c === 11
                                                                        ? "Combi"
                                                                        : c === 12
                                                                            ? "Coupe"
                                                                            : "Nespecificat"}
                      </option>
                  ))}
                </select>
                <span className="srCarsFilters_chevron" aria-hidden="true">
                <FontAwesomeIcon icon={faChevronDown} />
              </span>
              </div>

              <div className="srCarsFilters_range">
                <div className="srCarsFilters_rangeInner">
                  <div className="srCarsFilters_rangeLabel">
                    <FontAwesomeIcon icon={faCalendarAlt} aria-hidden="true" />
                    An de fabricare
                  </div>
                  <div className="srCarsFilters_rangeRow">
                    <input
                        className="srCarsFilters_input"
                        type="number"
                        placeholder="de la"
                        value={yearRange.min}
                        min={1990}
                        max={new Date().getFullYear()}
                        onChange={(e) =>
                            setYearRange((p) => ({
                              ...p,
                              min: e.target.value === "" ? "" : Number(e.target.value),
                            }))
                        }
                        aria-label="Anul minim"
                    />
                    <span className="srCarsFilters_dash" aria-hidden="true">—</span>
                    <input
                        className="srCarsFilters_input"
                        type="number"
                        placeholder="până la"
                        value={yearRange.max}
                        min={1990}
                        max={new Date().getFullYear()}
                        onChange={(e) =>
                            setYearRange((p) => ({
                              ...p,
                              max: e.target.value === "" ? "" : Number(e.target.value),
                            }))
                        }
                        aria-label="Anul maxim"
                    />
                  </div>
                </div>
              </div>

              <div className={`srCarsFilters_actions${hasActiveFilters ? " is-two" : ""}`}>
                <button type="submit" className="srCars_btn srCars_btnPrimary">
                  <FontAwesomeIcon icon={faSearch} aria-hidden="true" />
                  Aplică filtrele
                </button>
                {hasActiveFilters && (
                    <button
                        type="button"
                        className="srCars_btn srCars_btnGhost"
                        onClick={clearFilters}
                    >
                      <FontAwesomeIcon icon={faTimes} aria-hidden="true" />
                      Resetează
                    </button>
                )}
              </div>

            </form>
          </section>

          {noFilteredProductsMessage && (
              <div className="srCars_notice" role="alert" aria-live="polite">
                Nu au fost găsite vehicule pentru filtrele selectate.
              </div>
          )}

          <div className="srCars_sectionHead">
            <h2 className="srCars_sectionTitle">
              {isFiltering ? "Rezultate filtrare" : "Toate vehiculele"}
            </h2>
            <span className="srCars_sectionCount">
            {listToRender.length} vehicul{listToRender.length !== 1 ? "e" : ""}
          </span>
          </div>

          <div className="srCars_grid">
            {listToRender.map((product) => {
              const seats = product.locuri ?? 5;
              const cc = product.cilindree ? `${product.cilindree} ` : "";
              return (
                  <Link
                      to={`/product/${product.id}`}
                      key={product.id}
                      className="srCars_link"
                      aria-label={`${product.producator} ${product.name}`}
                  >
                    <article className="srCars_card">
                      {product.discount ? (
                          <span className="srCars_badgeDiscount" aria-label="Reducere">%</span>
                      ) : null}

                      <CardImageCarousel
                          images={product.images ?? []}
                          alt={`${product.producator} ${product.name}`}
                      />

                      <div className="srCars_body">
                        {/* Specs row: gearbox · seats · cc + fuel */}
                        <ul className="srCars_specs" aria-label="Caracteristici">
                          <li className="srCars_spec">
                            <FontAwesomeIcon icon={faCog} aria-hidden="true" />
                            <span>{gearboxLabel(product.cutia)}</span>
                          </li>
                          <li className="srCars_spec">
                            <FontAwesomeIcon icon={faUser} aria-hidden="true" />
                            <span>{seats} locuri</span>
                          </li>
                          <li className="srCars_spec">
                            <FontAwesomeIcon icon={faGasPump} aria-hidden="true" />
                            <span>{cc}{fuelLabel(product.motor)}</span>
                          </li>
                        </ul>

                        {/* Title */}
                        <h3 className="srCars_name">
                          {product.producator} {product.name}
                        </h3>

                        {/* Bottom: price (left) + CTA (right) */}
                        <div className="srCars_bottom">
                          <div className="srCars_priceWrap">
                            <span className="srCars_priceLabel">de la</span>
                            <span className="srCars_priceNum">{product.price1} €/zi</span>
                          </div>

                          <button className="srCars_ctaBtn" type="button">
                            » Chirie
                          </button>
                        </div>
                      </div>
                    </article>
                  </Link>
              );
            })}
          </div>

          {!isFiltering && (
              <nav className="srCars_pager" aria-label="Paginare">
                {currentPage > 1 && (
                    <button
                        type="button"
                        className="srCars_pagerBtn"
                        onClick={() => setCurrentPage((p) => p - 1)}
                        aria-label="Pagina anterioară"
                    >
                      <FontAwesomeIcon icon={faArrowLeft} />
                    </button>
                )}

                <ul className="srCars_pages">
                  {pageNumbers.map((n) => (
                      <li
                          key={n}
                          className={`srCars_pageNum${currentPage === n ? " is-active" : ""}`}
                          onClick={() => setCurrentPage(n)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setCurrentPage(n)}
                          aria-label={`Pagina ${n}`}
                          aria-current={currentPage === n ? "page" : undefined}
                      >
                        {n}
                      </li>
                  ))}
                </ul>

                {currentPage < totalPages && (
                    <button
                        type="button"
                        className="srCars_pagerBtn"
                        onClick={() => setCurrentPage((p) => p + 1)}
                        aria-label="Pagina următoare"
                    >
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                )}
              </nav>
          )}
        </div>
      </div>
  );
};

export default ProductList;
